import { Box, Paper, Typography } from "@mui/material";
import { useGetOne, useRecordContext } from "react-admin";
import { Client, DocumentRead, ResourceName, User, Vendor } from "@/types";
import { BORDER_RADIUS } from "@/constants/layout";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { formatDocumentCategory } from "../utils/documentUtils";

/** Vendor type word, same order LinkedVendorCard / the details drawer use. */
export const vendorTypeLabel = (vendor: Pick<Vendor, "is_sl_carrier" | "is_tpa" | "is_rx_vendor">): string | null =>
  vendor.is_sl_carrier ? "Carrier" : vendor.is_tpa ? "TPA" : vendor.is_rx_vendor ? "Rx/PBM" : null;

interface Fact {
  label: string;
  value: string;
}

/**
 * The facts the panel shows, in order. Pure so the degrade rules (#7553 AC2)
 * are unit-testable: no client → "Chosen at review"; an absent source (no
 * vendor, no uploader) drops its row rather than showing "—".
 */
export const buildInFlightFacts = ({
  documentRecord,
  client,
  vendor,
  uploader,
}: {
  documentRecord: DocumentRead;
  client: Client | undefined;
  vendor: Vendor | undefined;
  uploader: User | undefined;
}): Fact[] => {
  const facts: Fact[] = [];

  if (documentRecord.client_id_ref) {
    if (client)
      facts.push({ label: "Client", value: client.client_legal_name || client.client_short_name || "Client" });
  } else {
    facts.push({ label: "Client", value: "Chosen at review" });
  }

  if (documentRecord.vendor_id_ref && vendor) {
    const type = vendorTypeLabel(vendor);
    facts.push({ label: "Administrator", value: [vendor.company_name, type].filter(Boolean).join(" · ") });
  }

  const typeParts = [
    documentRecord.record_type ? formatDocumentCategory(String(documentRecord.record_type)) : null,
    documentRecord.file_format_name ?? null,
  ].filter((p): p is string => !!p);
  if (typeParts.length > 0) facts.push({ label: "Type", value: typeParts.join(" · ") });

  const uploaderName = uploader ? uploader.full_name || uploader.preferred_name || uploader.email : null;
  const uploadedAt = documentRecord.uploaded_datetime
    ? formatDate(String(documentRecord.uploaded_datetime), "MMM d, yyyy h:mm a")
    : null;
  if (uploaderName || uploadedAt) {
    facts.push({ label: "Uploaded by", value: [uploaderName, uploadedAt].filter(Boolean).join(" · ") });
  }

  return facts;
};

/**
 * Waiting panel for a document that is queued or extracting before review
 * (#7553): what happens next and the facts the reviewer will need. Download
 * lives in the page header; the audit log returns once the tabs do. Rendered by DocumentShow in place of the
 * Result / Mapping / Audit Log tabs; the in-flight trail sits above it.
 */
export const DocumentInFlightPanel = () => {
  const documentRecord = useRecordContext<DocumentRead>();
  const clientId = documentRecord?.client_id_ref;
  const vendorId = documentRecord?.vendor_id_ref;
  const uploaderId = documentRecord?.uploaded_by_user_id_ref;
  const { data: client } = useGetOne<Client>(ResourceName.CLIENTS, { id: clientId ?? "" }, { enabled: !!clientId });
  const { data: vendor } = useGetOne<Vendor>(ResourceName.VENDORS, { id: vendorId ?? "" }, { enabled: !!vendorId });
  const { data: uploader } = useGetOne<User>(ResourceName.USERS, { id: uploaderId ?? "" }, { enabled: !!uploaderId });

  if (!documentRecord) return null;

  const facts = buildInFlightFacts({ documentRecord, client, vendor, uploader });

  return (
    <Box pb={4} data-testid="document-in-flight-panel">
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: BORDER_RADIUS.LARGE, p: 3.5, maxWidth: 760 }}
      >
        <Typography variant="h5" component="h2" sx={{ mb: 0.75 }}>
          Getting this file ready for review
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          When extraction finishes this page becomes the review screen. You can leave; the Documents list shows when it
          is ready.
        </Typography>

        {facts.length > 0 && (
          <Box
            component="dl"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: 3,
              rowGap: 1,
              mt: 2.5,
              mb: 0,
            }}
          >
            {facts.map((fact) => (
              <Box key={fact.label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, minWidth: 0 }}>
                <Typography component="dt" variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {fact.label}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, textAlign: "right", minWidth: 0 }} noWrap>
                  {fact.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
